/**
 * @file useNavBar.js
 * @description 全站导航栏：分类跳转、用户卡片、互动消息未读
 * @module shared/composables/useNavBar
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { fetchUserBaseApi } from '@/features/user/api/userApi'
import {
  fetchInteractionUnreadCountApi,
  getInteractionUnreadStreamUrl,
} from '@/features/message/api/messageApi'
import { startInteractionUnreadStream } from '@/features/message/utils/interactionUnreadStream'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { REGION_CODE_JAPAN } from '@/features/comic/constants/comicClassifyFilters'

/**
 * @param {{ authToken: string }} props
 * @param {(event: 'show-login' | 'logout') => void} emit
 */
export function useNavBar(props, emit) {
  const router = useRouter()

  const user = ref(null)
  const isUserCardVisible = ref(false)
  const hideTimeout = ref(null)
  const isMenuOpen = ref(false)
  const isDropdownOpen = ref(false)
  const isRegionDropdownOpen = ref(false)
  const isMobile = ref(false)
  const isInteractionPopoverOpen = ref(false)
  const interactionUnreadCount = ref(0)

  const userAvatar = ref(null)
  const userCard = ref(null)
  const bellBtn = ref(null)
  const userArea = ref(null)

  let stopUnreadStream = null
  let unreadStreamRetryTimer = null

  const interactionUnreadDisplay = computed(() => {
    const n = Number(interactionUnreadCount.value) || 0
    if (n <= 0) return ''
    return n > 99 ? '99+' : String(n)
  })

  function resolveToken() {
    return (
      props.authToken ||
      JSON.parse(localStorage.getItem('user') || '{}')?.token ||
      ''
    )
  }

  async function fetchInteractionUnreadCount() {
    const token = resolveToken()
    if (!token) return
    try {
      const res = await fetchInteractionUnreadCountApi(token)
      if (res.data.code === 0 || res.data.code === 200) {
        const raw = res.data.data?.unreadCount
        interactionUnreadCount.value = Number(raw) || 0
      }
    } catch (e) {
      console.error('获取互动消息未读数失败', e)
    }
  }

  function stopInteractionUnreadStream() {
    if (stopUnreadStream) {
      stopUnreadStream()
      stopUnreadStream = null
    }
    if (unreadStreamRetryTimer) {
      clearTimeout(unreadStreamRetryTimer)
      unreadStreamRetryTimer = null
    }
  }

  function startInteractionUnreadStreamHandler() {
    stopInteractionUnreadStream()
    const token = resolveToken()
    if (!token) return
    let attempt = 0
    const connect = () => {
      if (!resolveToken()) return
      stopUnreadStream = startInteractionUnreadStream({
        url: getInteractionUnreadStreamUrl(),
        token,
        onUnreadCount: (n) => {
          interactionUnreadCount.value = n
          attempt = 0
        },
        onError: () => {
          stopUnreadStream = null
          if (!props.authToken) return
          attempt = Math.min(attempt + 1, 8)
          const delay = Math.min(
            8000,
            400 * Math.pow(2, Math.min(attempt - 1, 4))
          )
          unreadStreamRetryTimer = setTimeout(() => connect(), delay)
        },
        onEnd: () => {
          stopUnreadStream = null
          if (!props.authToken) return
          unreadStreamRetryTimer = setTimeout(() => connect(), 600)
        },
      })
    }
    connect()
  }

  async function fetchUserBase() {
    try {
      const response = await fetchUserBaseApi()
      user.value = response.data.baseInfoVo
    } catch (error) {
      console.error('获取用户信息失败：', error)
      user.value = null
    }
  }

  function toggleDropdown() {
    isDropdownOpen.value = !isDropdownOpen.value
  }

  function toggleRegionDropdown() {
    isRegionDropdownOpen.value = !isRegionDropdownOpen.value
  }

  function showDropdown() {
    isDropdownOpen.value = true
  }

  function hideDropdown() {
    isDropdownOpen.value = false
  }

  function goToCategory(themeArray) {
    const formattedThemeArray = themeArray.join(',')
    router.push({
      name: 'ComicSearch',
      query: {
        mode: 'classify',
        themeArray: formattedThemeArray,
      },
    })
    isDropdownOpen.value = false
    isMenuOpen.value = false
  }

  /**
   * 按地区跳转分类页
   * @param {string} regionCode classify 接口 regionCode
   */
  function goToRegion(regionCode) {
    router.push({
      name: 'ComicSearch',
      query: {
        mode: 'classify',
        regionCode: String(regionCode),
      },
    })
    isRegionDropdownOpen.value = false
    isMenuOpen.value = false
  }

  /** 导航「漫画地区 - 日本」 */
  function goToJapanRegion() {
    goToRegion(REGION_CODE_JAPAN)
  }

  function goToRequestComic() {
    router.push({ name: 'RequestComic' })
    isMenuOpen.value = false
  }

  function hideUserCard() {
    hideTimeout.value = setTimeout(() => {
      isUserCardVisible.value = false
    }, 100)
  }

  function toggleUserCard() {
    if (isMobile.value) {
      isUserCardVisible.value = !isUserCardVisible.value
    }
  }

  function handleUserAreaClick(event) {
    if (isMobile.value) {
      event.stopPropagation()
      toggleUserCard()
    }
  }

  function handleOverlayClick(event) {
    if (isMobile.value && isUserCardVisible.value) {
      event.preventDefault()
      event.stopPropagation()
      isUserCardVisible.value = false
    }
  }

  function handleMouseEnter(event) {
    if (isMobile.value) return
    const avatarEl = userAvatar.value
    if (avatarEl) {
      avatarEl.classList.remove('rotate-counterclockwise')
      avatarEl.classList.add('rotate-clockwise')
    }
    event.stopPropagation()
    clearTimeout(hideTimeout.value)
    isUserCardVisible.value = true
  }

  function handleMouseLeave(event) {
    if (isMobile.value) return
    const avatarEl = userAvatar.value
    if (avatarEl) {
      avatarEl.classList.remove('rotate-clockwise')
      avatarEl.classList.add('rotate-counterclockwise')
    }

    const userAreaEl = userArea.value
    const userCardEl = userCard.value?.$el
    if (
      userAreaEl?.contains(event.relatedTarget) ||
      userCardEl?.contains(event.relatedTarget)
    ) {
      return
    }
    event.stopPropagation()
    hideTimeout.value = setTimeout(() => {
      isUserCardVisible.value = false
    }, 200)
  }

  function goToHome() {
    router.push({ path: '/' })
  }

  function getBellAnchor() {
    return bellBtn.value
  }

  function ensureInteractionLoginOrShow() {
    const token = JSON.parse(localStorage.getItem('user') || '{}')?.token || ''
    if (!token) {
      emit('show-login')
      return false
    }
    return true
  }

  function openInteractionPopover() {
    if (!ensureInteractionLoginOrShow()) return
    isInteractionPopoverOpen.value = true
    isUserCardVisible.value = false
    isMenuOpen.value = false
  }

  function closeInteractionPopover() {
    isInteractionPopoverOpen.value = false
  }

  function handleBellClick() {
    if (!ensureInteractionLoginOrShow()) return
    if (isInteractionPopoverOpen.value) {
      closeInteractionPopover()
    } else {
      openInteractionPopover()
    }
  }

  function toggleMenu() {
    isMenuOpen.value = !isMenuOpen.value
    if (isMenuOpen.value) {
      isInteractionPopoverOpen.value = false
    }
    if (!isMenuOpen.value) {
      isDropdownOpen.value = false
      isRegionDropdownOpen.value = false
    }
  }

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
  }

  watch(
    () => props.authToken,
    (val) => {
      if (val) {
        fetchInteractionUnreadCount()
        startInteractionUnreadStreamHandler()
        fetchUserBase()
      } else {
        interactionUnreadCount.value = 0
        user.value = null
        stopInteractionUnreadStream()
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    const token = resolveToken()
    if (token && !user.value) {
      fetchUserBase()
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateIsMobile)
    stopInteractionUnreadStream()
  })

  return {
    user,
    isUserCardVisible,
    isMenuOpen,
    isDropdownOpen,
    isRegionDropdownOpen,
    isMobile,
    isInteractionPopoverOpen,
    interactionUnreadCount,
    interactionUnreadDisplay,
    userAvatar,
    userCard,
    bellBtn,
    userArea,
    toggleDropdown,
    toggleRegionDropdown,
    showDropdown,
    hideDropdown,
    goToCategory,
    goToJapanRegion,
    goToRequestComic,
    hideUserCard,
    handleUserAreaClick,
    handleOverlayClick,
    handleMouseEnter,
    handleMouseLeave,
    goToHome,
    getBellAnchor,
    fetchInteractionUnreadCount,
    handleBellClick,
    toggleMenu,
  }
}
